/* eslint-disable react-hooks/exhaustive-deps */
// Screen: "Euchre — Partner/Team game table"
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { connectSocket, sendGameState, sendPlayerAction, leaveRoom } from "./socketClient";
import { getApiBaseUrl } from "./utils/platform";
import { AI_NAMES, AI_PASS_SAYINGS, AI_ORDER_SAYINGS } from "./config/aiConfig";
import { getRankProgress } from "./components/EloBadge";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const API_BASE_URL = getApiBaseUrl();
const SUITS = ["♠", "♥", "♦", "♣"];
const SUIT_COLORS = { "♠": "#0f172a", "♥": "#dc2626", "♦": "#dc2626", "♣": "#0f172a" };
const SUIT_STROKE = {
  "♠": "0.8px rgba(30,64,175,0.55)",
  "♣": "0.8px rgba(21,128,61,0.55)",
  "♥": "0.6px rgba(185,28,28,0.4)",
  "♦": "0.6px rgba(185,28,28,0.4)",
};
const SUIT_PARTNER = { "♠": "♣", "♣": "♠", "♥": "♦", "♦": "♥" };
const RANKS = ["9", "10", "J", "Q", "K", "A"];
const RANK_ORDER = { "9": 0, "10": 1, J: 2, Q: 3, K: 4, A: 5 };
const PLAYER_COUNT = 4;
const ALL_PLAYERS = [0, 1, 2, 3];
const EUCHRE_WIN_SCORE = 10;
// Teams: seats 0+2 vs seats 1+3
const TEAM_OF = [0, 1, 0, 1]; // teamOf[seat] = team index
const TEAM_SEATS = [[0, 2], [1, 3]];
const DEAL_ANIM_MS = 220;
const FIND_DEAL_CARD_MS = 350;
const FIND_DEAL_WIN_PAUSE_MS = 1000;

function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ─── CARD UTILITIES ───────────────────────────────────────────────────────────
function buildDeck() {
  const deck = [];
  for (const s of SUITS) for (const r of RANKS) deck.push({ rank: r, suit: s });
  return deck;
}

function shuffle(arr) {
  let a = [...arr];
  for (let pass = 0; pass < 3; pass++) {
    const rv = new Uint32Array(a.length);
    crypto.getRandomValues(rv);
    for (let i = a.length - 1; i > 0; i--) {
      const range = i + 1;
      const limit = Math.floor(0x100000000 / range) * range;
      let r = rv[i];
      if (r >= limit) { const ex = new Uint32Array(1); do { crypto.getRandomValues(ex); r = ex[0]; } while (r >= limit); }
      const j = r % range;
      [a[i], a[j]] = [a[j], a[i]];
    }
  }
  return a;
}

function cardKey(c) { return c ? c.rank + c.suit : ""; }
function effectiveSuit(card, trump) {
  if (!trump) return card.suit;
  if (card.rank === "J" && card.suit === SUIT_PARTNER[trump]) return trump;
  return card.suit;
}
function isLeftBower(card, trump) { return card.rank === "J" && card.suit === SUIT_PARTNER[trump]; }
function isRightBower(card, trump) { return card.rank === "J" && card.suit === trump; }
// In Euchre, find dealer by first Jack dealt (any suit — not Buck Euchre's black-jack rule)
function isAnyJack(card) { return card && card.rank === "J"; }

function cardStrength(card, trump, ledSuit) {
  if (isRightBower(card, trump)) return 100;
  if (isLeftBower(card, trump)) return 99;
  const eSuit = effectiveSuit(card, trump);
  const base = RANK_ORDER[card.rank];
  if (eSuit === trump) return 50 + base;
  if (eSuit === ledSuit) return 20 + base;
  return base;
}

function sortHandForDisplay(hand, trump) {
  if (!hand || hand.length === 0) return hand;
  const SUIT_ORDER = { "♠": 0, "♥": 1, "♦": 2, "♣": 3 };
  return [...hand].sort((a, b) => {
    const aEff = effectiveSuit(a, trump);
    const bEff = effectiveSuit(b, trump);
    const aT = aEff === trump ? 1 : 0;
    const bT = bEff === trump ? 1 : 0;
    if (aT !== bT) return aT - bT;
    const aS = SUIT_ORDER[aEff] ?? 0;
    const bS = SUIT_ORDER[bEff] ?? 0;
    if (aS !== bS) return aS - bS;
    return (RANK_ORDER[a.rank] ?? 0) - (RANK_ORDER[b.rank] ?? 0);
  });
}

function nextPlayer(p) { return (p + 1) % PLAYER_COUNT; }

// ─── AI LOGIC ─────────────────────────────────────────────────────────────────
function aiTrumpRound1(hand, turnedUp, playerIdx, dealerIdx) {
  let trumpCount = 0; let hasRB = false; let hasLB = false;
  for (const c of hand) {
    if (effectiveSuit(c, turnedUp.suit) === turnedUp.suit) { trumpCount++; if (isRightBower(c, turnedUp.suit)) hasRB = true; if (isLeftBower(c, turnedUp.suit)) hasLB = true; }
  }
  const isDealer = playerIdx === dealerIdx;
  if (hasRB && hasLB) return true;
  if (hasRB && trumpCount >= 2) return true;
  if (trumpCount >= 3) return true;
  if (isDealer && trumpCount >= 2) return true;
  return false;
}

function aiTrumpRound2(hand, excludedSuit, playerIdx, dealerIdx) {
  const isDealer = playerIdx === dealerIdx;
  let best = null; let bestCount = 0;
  for (const s of SUITS) {
    if (s === excludedSuit) continue;
    let tc = 0;
    for (const c of hand) { if (effectiveSuit(c, s) === s) tc++; }
    if (tc > bestCount) { bestCount = tc; best = s; }
  }
  if (isDealer) return best; // dealer must call
  if (bestCount >= 2) return best;
  return null;
}

function aiDecideGoAlone(hand, trump) {
  // Count high trumps (RB, LB, Ace of trump, King of trump, Queen of trump)
  let highTrumps = 0;
  let offSuitAces = 0;
  const suits = new Set();
  for (const c of hand) {
    const eSuit = effectiveSuit(c, trump);
    suits.add(eSuit);
    if (eSuit === trump) {
      if (isRightBower(c, trump) || isLeftBower(c, trump) || c.rank === "A" || c.rank === "K" || c.rank === "Q") {
        highTrumps++;
      }
    } else if (c.rank === "A") {
      offSuitAces++;
    }
  }
  // Go alone if: 3+ high trumps AND (2+ off-suit aces OR only 2 suits)
  return highTrumps >= 3 && (offSuitAces >= 2 || suits.size <= 2);
}

function aiPlayCard(hand, trick, trump, ledSuit, playerIdx, partnerSeat, trumpCaller, teamOf) {
  const followable = hand.filter(c => effectiveSuit(c, trump) === ledSuit);
  const playable = followable.length > 0 ? followable : hand;
  const iCalledTrump = playerIdx === trumpCaller;
  const partnerCalledTrump = partnerSeat === trumpCaller;

  // ── FOLLOWING a led card ──
  if (trick.length > 0 && ledSuit) {
    let topStr = -1; let topPlayer = -1;
    for (const t of trick) {
      const s = cardStrength(t.card, trump, ledSuit);
      if (s > topStr) { topStr = s; topPlayer = t.player; }
    }
    const partnerWinning = topPlayer === partnerSeat;

    if (partnerWinning && playable.length > 1) {
      // Special case: I called trump and partner returned a small trump — take control
      // Partner is trying to give me the lead; honour it by winning with my high trump
      if (iCalledTrump && ledSuit === trump) {
        const canBeat = playable.filter(c => cardStrength(c, trump, ledSuit) > topStr);
        if (canBeat.length > 0) {
          canBeat.sort((a, b) => cardStrength(a, trump, ledSuit) - cardStrength(b, trump, ledSuit));
          return canBeat[0]; // take with lowest winning card to preserve high trump
        }
      }
      // Otherwise partner is winning — dump our lowest
      const sorted = [...playable].sort((a, b) => cardStrength(a, trump, ledSuit) - cardStrength(b, trump, ledSuit));
      return sorted[0];
    }
    const canBeat = playable.filter(c => cardStrength(c, trump, ledSuit) > topStr);
    if (canBeat.length > 0) {
      canBeat.sort((a, b) => cardStrength(a, trump, ledSuit) - cardStrength(b, trump, ledSuit));
      return canBeat[0];
    }
    playable.sort((a, b) => cardStrength(a, trump, ledSuit) - cardStrength(b, trump, ledSuit));
    return playable[0];
  }

  // ── LEADING ──
  const offSuit = hand.filter(c => effectiveSuit(c, trump) !== trump);
  const trumpCards = hand.filter(c => effectiveSuit(c, trump) === trump);

  if (iCalledTrump) {
    // I called trump: lead trump to draw it out (bleed), prefer highest
    if (trumpCards.length >= 2) {
      trumpCards.sort((a, b) => cardStrength(b, trump, trump) - cardStrength(a, trump, trump));
      return trumpCards[0];
    }
  }

  if (partnerCalledTrump && trumpCards.length >= 1) {
    // Partner called trump — return trump to them so they can take control.
    // Lead our LOWEST trump (probably a 9/10/Q/K) so partner can win with their high trump.
    trumpCards.sort((a, b) => cardStrength(a, trump, trump) - cardStrength(b, trump, trump));
    return trumpCards[0];
  }

  // Default: lead highest off-suit (Aces are strongest, never lead trump if avoidable)
  if (offSuit.length > 0) {
    offSuit.sort((a, b) => cardStrength(b, trump, null) - cardStrength(a, trump, null));
    return offSuit[0];
  }
  // Only trump left — lead lowest to preserve best trump
  trumpCards.sort((a, b) => cardStrength(a, trump, trump) - cardStrength(b, trump, trump));
  return trumpCards[0];
}

// ─── SCORE EUCHRE HAND ────────────────────────────────────────────────────────
function scoreEuchreHand(tricksWon, trumpCaller, goingAlone, lonerSeat, teamOf) {
  const team0Tricks = (tricksWon[0] || 0) + (tricksWon[2] || 0);
  const team1Tricks = (tricksWon[1] || 0) + (tricksWon[3] || 0);
  const callerTeam = teamOf[trumpCaller];
  const oppTeam = 1 - callerTeam;
  const callerTricks = callerTeam === 0 ? team0Tricks : team1Tricks;
  const delta = [0, 0];

  if (goingAlone) {
    if (callerTricks === 5) { delta[callerTeam] = 4; return { delta, outcome: "loner_march" }; }
    if (callerTricks >= 3) { delta[callerTeam] = 1; return { delta, outcome: "made_it" }; }
    delta[oppTeam] = 2; return { delta, outcome: "euchred" };
  }

  if (callerTricks === 5) { delta[callerTeam] = 2; return { delta, outcome: "march" }; }
  if (callerTricks >= 3) { delta[callerTeam] = 1; return { delta, outcome: "made_it" }; }
  delta[oppTeam] = 2; return { delta, outcome: "euchred" };
}

const OUTCOME_LABELS = {
  loner_march: "Loner March! +4 pts",
  march: "March! +2 pts",
  made_it: "Made it! +1 pt",
  euchred: "Euchred! Opponents +2 pts",
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function Euchre() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: currentUser, token, isAuthenticated, signout, updateUser } = useAuth();

  // Multiplayer params
  const communityCode = searchParams.get("community");
  const isMultiplayer = !!communityCode;
  const mySeatParam = parseInt(searchParams.get("seat") ?? "0");
  const isHost = searchParams.get("host") === "true";
  const isLivePlay = searchParams.get("liveplay") === "true";
  const partnerMode = searchParams.get("partnerMode") || "fixed";
  const seatedParam = searchParams.get("seated");
  const multiplayerPlayers = seatedParam ? JSON.parse(seatedParam) : [];

  // Seat rotation: local human always sees themselves at bottom (seat 0 visually)
  const rotOffset = isMultiplayer ? mySeatParam : 0;
  function actualSeat(displaySeat) { return (displaySeat + rotOffset) % PLAYER_COUNT; }
  function displaySeat(actual) { return (actual - rotOffset + PLAYER_COUNT) % PLAYER_COUNT; }

  const HUMAN = isMultiplayer ? mySeatParam : 0;
  const mySeat = HUMAN;

  const [lpRemoteHumans, setLpRemoteHumans] = useState([]); // live play seat additions (moved up for use in remoteHumanSeats)

  // Seats occupied by OTHER human players (not AI, not me) — host skips AI for these
  const remoteHumanSeats = isMultiplayer
    ? [...new Set([
        ...multiplayerPlayers.filter(p => p.seatIndex !== mySeat).map(p => p.seatIndex),
        ...lpRemoteHumans,
      ])]
    : [];

  // AI names for non-human seats — randomly shuffle pool each game
  const [aiPlayerNames, setAiPlayerNames] = useState(() => {
    const names = {};
    if (isMultiplayer) {
      ALL_PLAYERS.forEach(i => {
        const mp = multiplayerPlayers.find(p => p.seatIndex === i);
        names[i] = mp ? mp.nickname : AI_NAMES[i % AI_NAMES.length];
      });
    } else {
      const pool = [...AI_NAMES].sort(() => Math.random() - 0.5);
      let aiIdx = 0;
      ALL_PLAYERS.forEach(i => {
        names[i] = i === 0 ? (currentUser?.nickname || "You") : pool[aiIdx++ % pool.length];
      });
    }
    return names;
  });

  function playerLabel(seat) { return aiPlayerNames[seat] || `P${seat}`; }
  function teamLabel(teamIdx) {
    return teamSeats[teamIdx].map(s => playerLabel(s)).join(" / ");
  }

  // Sockets for multiplayer
  const socketRef = useRef(null);
  useEffect(() => {
    if (!isMultiplayer || !token) return;
    const sock = connectSocket(token);
    socketRef.current = sock;

    if (isLivePlay && isHost && communityCode) {
      sock.emit('liveplay:register', { roomCode: communityCode });
    }
    if (isLivePlay && !isHost && communityCode) {
      sock.emit('liveplay:join', { roomCode: communityCode, seatIndex: mySeatParam });
    }
    sock.on('liveplay:room_closed', () => {
      alert('The Live Play table has closed. Returning home.');
      navigate('/home');
    });
    return () => {
      if (isLivePlay && communityCode) sock.emit('liveplay:leave', { roomCode: communityCode, seatIndex: mySeatParam });
    };
  }, [isMultiplayer, token]);

  // Live Play host: signal between-hands + accept seat swaps
  useEffect(() => {
    if (!isLivePlay || !isHost || !communityCode) return;
    const sock = socketRef.current; if (!sock) return;
    const handler = ({ seatIndex, nickname, socketId, userId }) => {
      setLpRemoteHumans(prev => prev.includes(seatIndex) ? prev : [...prev, seatIndex]);
      setAiPlayerNames(prev => ({ ...prev, [seatIndex]: nickname }));
      console.log(`[LivePlay] ${nickname} took seat ${seatIndex} in Euchre`);
    };
    sock.on('liveplay:seat_taken', handler);
    return () => sock.off('liveplay:seat_taken', handler);
  }, [isLivePlay, isHost]);

  // ─── GAME STATE ─────────────────────────────────────────────────────────────
  const [gameId, setGameId] = useState(() => uuidv4());
  const [phase, setPhase] = useState("idle");
  const [hands, setHands] = useState([[], [], [], []]);
  const [dealer, setDealer] = useState(0);
  const [trump, setTrump] = useState(null);
  const [turnedUp, setTurnedUp] = useState(null);
  const [trumpCaller, setTrumpCaller] = useState(null);
  const [goingAlone, setGoingAlone] = useState(false);
  const [lonerSeat, setLonerSeat] = useState(null);
  const [currentTrick, setCurrentTrick] = useState([]);
  const [tricksWon, setTricksWon] = useState([0, 0, 0, 0]);
  const [teamScores, setTeamScores] = useState([0, 0]);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [whoseTurn, setWhoseTurn] = useState(null);
  const [round1Passes, setRound1Passes] = useState(0);
  const [round2Passes, setRound2Passes] = useState(0);
  const [playingPlayers, setPlayingPlayers] = useState([0, 1, 2, 3]);
  const [handNumber, setHandNumber] = useState(0);
  const [handResult, setHandResult] = useState(null);
  const [trickPauseCards, setTrickPauseCards] = useState(null);
  const [aiComment, setAiComment] = useState(null);
  const [aiTick, setAiTick] = useState(0);
  const [dealerWinBanner, setDealerWinBanner] = useState(null);
  const [findDealerState, setFindDealerState] = useState(null);
  // Dynamic team assignments — overridden by 'jacks' partner mode
  const [teamOf, setTeamOf] = useState(TEAM_OF); // [0,1,0,1] by default
  // Compute team seat lists from current teamOf (changes with jacks partner mode)
  const teamSeats = [
    ALL_PLAYERS.filter(s => teamOf[s] === 0),
    ALL_PLAYERS.filter(s => teamOf[s] === 1),
  ];
  const [findPartnerState, setFindPartnerState] = useState(null); // { dealt, currentPlayer, jacks: [seat, seat?] }
  const [dealAnim, setDealAnim] = useState(null);
  const [sortCards, setSortCards] = useState(false);
  // Speed / auto-deal controls
  const [aiSpeedUp, setAiSpeedUp] = useState(false);
  const [autoDeal, setAutoDeal] = useState(false);
  const [showMobileSettings, setShowMobileSettings] = useState(false);
  // Hand history (tricks played per hand)
  const [handHistory, setHandHistory] = useState([]);
  const [currentTricks, setCurrentTricks] = useState([]); // tricks in current hand
  const [showHandHistory, setShowHandHistory] = useState(false);
  const [showLastTrick, setShowLastTrick] = useState(false);
  const [showScorecardOverlay, setShowScorecardOverlay] = useState(false);

  const teamScoresRef = useRef(teamScores);
  useEffect(() => { teamScoresRef.current = teamScores; }, [teamScores]);

  // ── Save game + award RP when game ends ──────────────────────────────────────
  const gameSavedRef = useRef(false);
  useEffect(() => {
    if (phase !== "gameOver" || !isAuthenticated || !token || gameSavedRef.current) return;
    gameSavedRef.current = true;
    const winTeam = teamScores[0] >= EUCHRE_WIN_SCORE ? 0 : 1;
    const humanTeam = teamOf[mySeat];
    const won = humanTeam === winTeam;
    // Build minimal game log that the server's updateRankAfterGame path needs
    const players = ALL_PLAYERS.map(seat => ({
      playerIndex: seat,
      isAI: isMultiplayer ? !multiplayerPlayers.some(p => p.seatIndex === seat) : seat !== mySeat,
      userId: seat === mySeat ? currentUser?.userId : null,
      nickname: seat === mySeat ? (currentUser?.nickname || "You") : aiPlayerNames[seat],
      won: teamOf[seat] === winTeam ? 1 : 0,
    }));
    const gameLog = [{ move_type: "game_over", winner: mySeat, euchre_won: won, timestamp: new Date().toISOString() }];
    fetch(`${API_BASE_URL}/games/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ gameId, playerCount: PLAYER_COUNT, wagerMode: "none", gameLog, players, gameType: "euchre" }),
    })
    .then(r => r.json())
    .then(result => {
      if (result.success && result.rankPoints !== undefined) {
        updateUser({
          rankPoints: result.rankPoints,
          rankName: result.rankName,
          activeRP: result.activeRP,
          activeToNextSuit: result.activeToNextSuit,
          nextSuitLevel: result.nextSuitLevel,
          suitLevel: result.suitLevel,
          suitSymbol: result.suitSymbol,
          suitLevelProgress: result.suitLevelProgress,
          cardRankId: result.cardRankId,
          displayRank: result.displayRank,
          lifetimeRP: result.lifetimeRP,
        });
      }
    })
    .catch(() => {});
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps
  const trumpRef = useRef(trump);
  useEffect(() => { trumpRef.current = trump; }, [trump]);
  const trumpCallerRef = useRef(trumpCaller);
  useEffect(() => { trumpCallerRef.current = trumpCaller; }, [trumpCaller]);
  const turnedUpRef = useRef(turnedUp);
  useEffect(() => { turnedUpRef.current = turnedUp; }, [turnedUp]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  // Responsive card sizes (matches BuckEuchre)
  const cardSize = isMobile
    ? { w: 54, h: 78, fontSize: 12, suitSize: 30 }
    : { w: 68, h: 100, fontSize: 13, suitSize: 38 };

  // Table dimensions
  const MT_W = isMobile ? 170 : 580;
  const MT_H = isMobile ? 350 : 380;

  // ─── MULTIPLAYER: host serializes and broadcasts state ───────────────────────
  function serializeGameState() {
    return {
      phase, whoseTurn, hands, dealer, trump, turnedUp, trumpCaller,
      goingAlone, lonerSeat, currentTrick, tricksWon, teamScores, scoreHistory,
      round1Passes, round2Passes, playingPlayers, handNumber, handResult,
      trickPauseCards, teamOf, findDealerState, findPartnerState, dealerWinBanner,
      aiPlayerNames: Object.values(aiPlayerNames),
      lpRemoteHumans,
    };
  }

  // HOST: Broadcast state on every relevant change
  useEffect(() => {
    if (!isMultiplayer || !isHost) return;
    sendGameState(serializeGameState());
  }, [phase, whoseTurn, hands, dealer, trump, turnedUp, trumpCaller, goingAlone,
      lonerSeat, currentTrick, tricksWon, teamScores, round1Passes, round2Passes,
      playingPlayers, handNumber, handResult, trickPauseCards, teamOf,
      findDealerState, findPartnerState, dealerWinBanner, lpRemoteHumans, aiPlayerNames]);

  // NON-HOST: Receive state from host and apply it
  useEffect(() => {
    if (!isMultiplayer || isHost) return;
    const sock = socketRef.current;
    if (!sock) return;
    const applyState = (state) => {
      setPhase(state.phase);
      setWhoseTurn(state.whoseTurn);
      setHands(state.hands);
      setDealer(state.dealer);
      setTrump(state.trump);
      setTurnedUp(state.turnedUp);
      setTrumpCaller(state.trumpCaller);
      setGoingAlone(state.goingAlone);
      setLonerSeat(state.lonerSeat);
      setCurrentTrick(state.currentTrick || []);
      setTricksWon(state.tricksWon || [0,0,0,0]);
      setTeamScores(state.teamScores || [0,0]);
      setScoreHistory(state.scoreHistory || []);
      setRound1Passes(state.round1Passes || 0);
      setRound2Passes(state.round2Passes || 0);
      setPlayingPlayers(state.playingPlayers || [0,1,2,3]);
      setHandNumber(state.handNumber || 0);
      setHandResult(state.handResult || null);
      setTrickPauseCards(state.trickPauseCards || null);
      setTeamOf(state.teamOf || TEAM_OF);
      setFindDealerState(state.findDealerState || null);
      setFindPartnerState(state.findPartnerState || null);
      setDealerWinBanner(state.dealerWinBanner || null);
      if (state.lpRemoteHumans) setLpRemoteHumans(state.lpRemoteHumans);
      if (state.aiPlayerNames) setAiPlayerNames(prev => {
        const updated = { ...prev };
        state.aiPlayerNames.forEach((name, i) => { if (name) updated[i] = name; });
        return updated;
      });
    };
    sock.on('game-state', applyState);
    return () => sock.off('game-state', applyState);
  }, [isMultiplayer, isHost]);

  // HOST: Listen for remote player actions
  useEffect(() => {
    if (!isMultiplayer || !isHost) return;
    const sock = socketRef.current;
    if (!sock) return;
    const onAction = (action) => {
      const { type, fromSeat } = action;
      if (fromSeat === undefined) return;
      if (type === 'orderUp' && phase === 'round1' && whoseTurn === fromSeat)
        handleTrumpRound1(fromSeat === dealer ? 'pick_up' : 'order_up', fromSeat);
      else if (type === 'pass' && phase === 'round1' && whoseTurn === fromSeat)
        handleTrumpRound1('pass', fromSeat);
      else if (type === 'callTrump' && phase === 'round2' && whoseTurn === fromSeat)
        handleTrumpRound2(action.suit, fromSeat);
      else if (type === 'pass' && phase === 'round2' && whoseTurn === fromSeat)
        handleTrumpRound2('pass', fromSeat);
      else if (type === 'goAlone' && phase === 'going_alone' && whoseTurn === fromSeat)
        handleGoAlone(action.alone, fromSeat);
      else if (type === 'discard' && phase === 'discard' && whoseTurn === fromSeat) {
        const card = hands[fromSeat]?.find(c => c.rank === action.card.rank && c.suit === action.card.suit);
        if (card) performDiscard(fromSeat, card);
      } else if (type === 'playCard' && phase === 'playing' && whoseTurn === fromSeat) {
        const card = hands[fromSeat]?.find(c => c.rank === action.card.rank && c.suit === action.card.suit);
        if (card) performPlay(fromSeat, card);
      }
    };
    sock.on('player-action', onAction);
    return () => sock.off('player-action', onAction);
  }, [isMultiplayer, isHost, phase, whoseTurn, hands, dealer]);

  // ─── SETUP ──────────────────────────────────────────────────────────────────
  function handleStart() {
    setGameId(uuidv4()); // new game = new id so RP isn't double-counted on Play Again
    gameSavedRef.current = false;
    setTeamOf(TEAM_OF); // reset to default before any new partner assignment
    if (partnerMode === 'jacks' && !isMultiplayer) {
      setPhase("findPartners");
      setFindPartnerState({ dealt: ALL_PLAYERS.map(() => []), currentPlayer: 0, jacks: [] });
    } else {
      setPhase("findDealer");
      setFindDealerState({ dealt: ALL_PLAYERS.map(() => []), currentPlayer: 0, winner: null });
    }
  }

  // Find dealer: deal one card at a time until any Jack (Euchre rule — not Buck Euchre's black-jack)
  useEffect(() => {
    if (isMultiplayer && !isHost) return; // non-host: state driven by host broadcast
    if (phase !== "findDealer" || !findDealerState || findDealerState.winner !== null) return;
    const deck = shuffle(buildDeck());
    let deckIdx = findDealerState.dealt.reduce((s, h) => s + h.length, 0);
    const card = deck[deckIdx % deck.length];
    const t = setTimeout(() => {
      const p = findDealerState.currentPlayer;
      const newDealt = findDealerState.dealt.map((h, i) => i === p ? [...h, card] : h);
      if (isAnyJack(card)) {
        setDealerWinBanner(`${playerLabel(p)} deals first!`);
        setFindDealerState({ dealt: newDealt, currentPlayer: p, winner: p });
        setTimeout(() => {
          setDealer(p); setDealerWinBanner(null); setFindDealerState(null);
          dealHand(p, teamScoresRef.current, 1);
        }, FIND_DEAL_WIN_PAUSE_MS);
      } else {
        setFindDealerState({ dealt: newDealt, currentPlayer: nextPlayer(p), winner: null });
      }
    }, FIND_DEAL_CARD_MS);
    return () => clearTimeout(t);
  }, [phase, findDealerState]);

  // Find partners via first-2-jacks: deal until 2 jacks found, those seats = Team 0, others = Team 1
  useEffect(() => {
    if (isMultiplayer && !isHost) return; // non-host: state driven by host broadcast
    if (phase !== "findPartners" || !findPartnerState || findPartnerState.jacks.length >= 2) return;
    const deck = shuffle(buildDeck());
    let deckIdx = findPartnerState.dealt.reduce((s, h) => s + h.length, 0);
    const card = deck[deckIdx % deck.length];
    const t = setTimeout(() => {
      const p = findPartnerState.currentPlayer;
      const newDealt = findPartnerState.dealt.map((h, i) => i === p ? [...h, card] : h);
      const newJacks = isAnyJack(card) ? [...findPartnerState.jacks, p] : findPartnerState.jacks;
      if (newJacks.length === 2) {
        // Partners found — team 0 = the two jack seats, team 1 = the other two
        const team0 = newJacks;
        const newTeamOf = ALL_PLAYERS.map(s => team0.includes(s) ? 0 : 1);
        setTeamOf(newTeamOf);
        const p0Label = playerLabel(newJacks[0]);
        const p1Label = playerLabel(newJacks[1]);
        setDealerWinBanner(`${p0Label} & ${p1Label} are partners!`);
        setFindPartnerState({ dealt: newDealt, currentPlayer: p, jacks: newJacks });
        setTimeout(() => {
          setDealerWinBanner(null);
          setFindPartnerState(null);
          setPhase("findDealer");
          setFindDealerState({ dealt: ALL_PLAYERS.map(() => []), currentPlayer: 0, winner: null });
        }, FIND_DEAL_WIN_PAUSE_MS * 2);
      } else {
        setFindPartnerState({ dealt: newDealt, currentPlayer: nextPlayer(p), jacks: newJacks });
      }
    }, FIND_DEAL_CARD_MS);
    return () => clearTimeout(t);
  }, [phase, findPartnerState]);

  function dealHand(dealerIdx, currentTeamScores, handNum) {
    const deck = shuffle(buildDeck());
    const finalHands = ALL_PLAYERS.map(() => []);
    const order = [];
    let p = nextPlayer(dealerIdx);
    for (let i = 0; i < PLAYER_COUNT; i++) { order.push(p); p = nextPlayer(p); }
    let idx = 0;
    for (const pl of order) { finalHands[pl] = deck.slice(idx, idx + 3); idx += 3; }
    for (const pl of order) { finalHands[pl].push(...deck.slice(idx, idx + 2)); idx += 2; }
    const kittyTop = deck[idx];

    setHandNumber(handNum);
    setTrump(null); setTurnedUp(null); setTrumpCaller(null);
    setGoingAlone(false); setLonerSeat(null);
    setCurrentTrick([]); setTricksWon([0, 0, 0, 0]);
    setRound1Passes(0); setRound2Passes(0);
    setPlayingPlayers([0, 1, 2, 3]);
    setHandResult(null); setTrickPauseCards(null);
    setCurrentTricks([]);

    const seq = [];
    for (const pl of order) for (let c = 0; c < 3; c++) seq.push({ player: pl, card: finalHands[pl][c] });
    for (const pl of order) for (let c = 3; c < 5; c++) seq.push({ player: pl, card: finalHands[pl][c] });

    setPhase("dealing");
    setHands(ALL_PLAYERS.map(() => []));
    setDealAnim({ seq, idx: 0, finalHands, kittyTop, dealerIdx });
  }

  // Deal animation
  useEffect(() => {
    if (isMultiplayer && !isHost) return; // non-host: state driven by host broadcast
    if (phase !== "dealing" || !dealAnim) return;
    if (dealAnim.idx >= dealAnim.seq.length) {
      setHands(dealAnim.finalHands);
      setTurnedUp(dealAnim.kittyTop);
      setDealer(dealAnim.dealerIdx);
      setPhase("round1");
      setWhoseTurn(nextPlayer(dealAnim.dealerIdx));
      setAiTick(x => x + 1);
      setDealAnim(null);
      return;
    }
    const t = setTimeout(() => {
      const { player, card } = dealAnim.seq[dealAnim.idx];
      setHands(prev => prev.map((h, i) => i === player ? [...h, card] : h));
      setDealAnim(prev => ({ ...prev, idx: prev.idx + 1 }));
    }, aiSpeedUp ? 60 : DEAL_ANIM_MS);
    return () => clearTimeout(t);
  }, [phase, dealAnim, aiSpeedUp]);

  // Auto-deal next hand
  useEffect(() => {
    if (phase !== "handOver" || !autoDeal) return;
    if (isMultiplayer && !isHost) return;
    const t = setTimeout(() => nextHand(), 1500);
    return () => clearTimeout(t);
  }, [phase, autoDeal]);

  // ─── AI TICK ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!["round1", "round2", "discard", "going_alone", "playing"].includes(phase)) return;
    if (whoseTurn === null) return;
    const isLocalHuman = isMultiplayer ? (whoseTurn === mySeat) : (whoseTurn === HUMAN);
    if (isLocalHuman) return;
    if (isMultiplayer && remoteHumanSeats.includes(whoseTurn)) return; // host waits for remote player action
    if (isMultiplayer && !isHost) return;

    // Round1: keep a human-visible pause even in speed mode so the turned-up card can be seen
    const delay = phase === "round1" ? (aiSpeedUp ? 900 : 1200) : aiSpeedUp ? 350 : 650;
    const t = setTimeout(() => {
      if (phase === "round1") {
        const call = aiTrumpRound1(hands[whoseTurn], turnedUp, whoseTurn, dealer);
        const decision = call ? (whoseTurn === dealer ? "pick_up" : "order_up") : "pass";
        const sayings = call ? AI_ORDER_SAYINGS : AI_PASS_SAYINGS;
        // setAiComment({ playerIdx: whoseTurn, text: sayings[Math.floor(Math.random() * sayings.length)], type: call ? "order" : "pass" });
        // setTimeout(() => setAiComment(null), 2000);
        handleTrumpRound1(decision, whoseTurn);
      } else if (phase === "round2") {
        const suit = aiTrumpRound2(hands[whoseTurn], turnedUpRef.current?.suit, whoseTurn, dealer);
        if (suit) {
          // setAiComment({ playerIdx: whoseTurn, text: AI_ORDER_SAYINGS[Math.floor(Math.random() * AI_ORDER_SAYINGS.length)], type: "order" });
          // setTimeout(() => setAiComment(null), 2000);
          handleTrumpRound2(suit, whoseTurn);
        } else {
          handleTrumpRound2("pass", whoseTurn);
        }
      } else if (phase === "going_alone") {
        const alone = aiDecideGoAlone(hands[whoseTurn], trump);
        handleGoAlone(alone, whoseTurn);
      } else if (phase === "discard") {
        aiDoDiscard(whoseTurn);
      } else if (phase === "playing") {
        aiDoPlay(whoseTurn);
      }
    }, delay);
    return () => clearTimeout(t);
  }, [phase, whoseTurn, aiTick]);

  // ─── TRUMP ROUND 1 ──────────────────────────────────────────────────────────
  function handleTrumpRound1(decision, pid) {
    if (decision === "pick_up" || decision === "order_up") {
      const newHands = hands.map((h, i) => i === dealer ? [...h, turnedUp] : h);
      setHands(newHands);
      setTrump(turnedUp.suit);
      setTrumpCaller(pid);
      setPhase("discard");
      setWhoseTurn(dealer);
      setAiTick(x => x + 1);
      return;
    }
    const np = round1Passes + 1;
    setRound1Passes(np);
    if (np >= PLAYER_COUNT) {
      setPhase("round2"); setRound2Passes(0); setWhoseTurn(nextPlayer(dealer)); setAiTick(x => x + 1);
    } else {
      setWhoseTurn(nextPlayer(pid)); setAiTick(x => x + 1);
    }
  }

  // ─── TRUMP ROUND 2 ──────────────────────────────────────────────────────────
  function handleTrumpRound2(decision, pid) {
    if (decision !== "pass" && decision === turnedUpRef.current?.suit) return; // can't call turned-down suit
    if (decision !== "pass") {
      setTrump(decision);
      setTrumpCaller(pid);
      setPhase("going_alone");
      setWhoseTurn(pid);
      setAiTick(x => x + 1);
      return;
    }
    const np = round2Passes + 1;
    setRound2Passes(np);
    if (np >= PLAYER_COUNT) {
      const nd = nextPlayer(dealer);
      setDealer(nd);
      dealHand(nd, teamScoresRef.current, handNumber + 1);
      return;
    }
    setWhoseTurn(nextPlayer(pid)); setAiTick(x => x + 1);
  }

  function aiDoDiscard(pid) {
    const h = hands[pid];
    const nonTrump = h.filter(c => effectiveSuit(c, trump) !== trump);
    const pool = nonTrump.length > 0 ? nonTrump : h;
    pool.sort((a, b) => cardStrength(a, trump, null) - cardStrength(b, trump, null));
    performDiscard(pid, pool[0]);
  }

  function performDiscard(pid, card) {
    setHands(prev => prev.map((h, i) => i === pid ? h.filter(c => cardKey(c) !== cardKey(card)) : h));
    setPhase("going_alone");
    setWhoseTurn(trumpCaller !== null ? trumpCaller : pid);
    setAiTick(x => x + 1);
  }

  // ─── GOING ALONE ────────────────────────────────────────────────────────────
  function handleGoAlone(alone, pid) {
    let sittingOutSeat = null;
    if (alone) {
      const callerTeam = teamOf[pid];
      sittingOutSeat = teamSeats[callerTeam].find(s => s !== pid);
      setGoingAlone(true);
      setLonerSeat(sittingOutSeat);
      setPlayingPlayers(ALL_PLAYERS.filter(s => s !== sittingOutSeat));
    } else {
      setGoingAlone(false);
      setLonerSeat(null);
      setPlayingPlayers([0, 1, 2, 3]);
    }
    setPhase("playing");
    setCurrentTrick([]);
    // Always skip the sitting-out partner when finding who leads first
    let firstPlayer = nextPlayer(dealer);
    if (sittingOutSeat !== null) {
      while (firstPlayer === sittingOutSeat) firstPlayer = nextPlayer(firstPlayer);
    }
    setWhoseTurn(firstPlayer);
    setAiTick(x => x + 1);
  }

  // ─── CARD PLAY ──────────────────────────────────────────────────────────────
  function ledSuit() {
    return currentTrick.length > 0 ? effectiveSuit(currentTrick[0].card, trump) : null;
  }

  function partnerOf(seat) {
    return teamSeats[teamOf[seat]].find(s => s !== seat);
  }

  function aiDoPlay(pid) {
    const h = hands[pid];
    const ls = ledSuit();
    const card = aiPlayCard(h, currentTrick, trump, ls, pid, partnerOf(pid), trumpCaller, teamOf);
    performPlay(pid, card);
  }

  function performPlay(pid, card) {
    const newTrick = [...currentTrick, { player: pid, card }];
    setCurrentTrick(newTrick);
    setHands(prev => prev.map((h, i) => i === pid ? h.filter(c => cardKey(c) !== cardKey(card)) : h));

    const active = playingPlayers;
    if (newTrick.length >= active.length) {
      // Trick complete — find winner
      const ls = effectiveSuit(newTrick[0].card, trump);
      let topStr = -1; let winner = newTrick[0].player;
      for (const t of newTrick) {
        const s = cardStrength(t.card, trump, ls);
        if (s > topStr) { topStr = s; winner = t.player; }
      }
      const newTricksWon = tricksWon.map((v, i) => i === winner ? v + 1 : v);
      setTrickPauseCards(newTrick);

      // Save trick to current hand history
      const trickRecord = { cards: newTrick.map(t => ({ player: t.player, card: t.card })), winner };
      setCurrentTricks(prev => [...prev, trickRecord]);

      setTimeout(() => {
        setTrickPauseCards(null);
        setCurrentTrick([]);
        setTricksWon(newTricksWon);
        const totalTricks = newTricksWon.reduce((s, v) => s + v, 0);
        if (totalTricks >= 5) {
          resolveHand(newTricksWon);
        } else {
          setWhoseTurn(winner);
          setAiTick(x => x + 1);
        }
      }, aiSpeedUp ? 400 : 1200);
    } else {
      const pidIdx = active.indexOf(pid);
      const nextIdx = (pidIdx + 1) % active.length;
      setWhoseTurn(active[nextIdx]);
      setAiTick(x => x + 1);
    }
  }

  // ─── RESOLVE HAND ───────────────────────────────────────────────────────────
  function resolveHand(tw) {
    const { delta, outcome } = scoreEuchreHand(tw, trumpCaller, goingAlone, lonerSeat, teamOf);
    const newScores = teamScores.map((s, i) => s + delta[i]);
    setTeamScores(newScores);
    setScoreHistory(prev => [...prev, { hand: handNumber, team0Delta: delta[0], team1Delta: delta[1], outcome }]);
    setHandResult({ outcome, delta, tricksWon: tw });

    // Save to hand history (tricks recorded via setCurrentTricks during play)
    setHandHistory(prev => [...prev, {
      handNumber,
      trump: trumpRef.current,
      trumpCaller: trumpCallerRef.current,
      outcome,
      tricks: [] // will be filled by currentTricks flush below
    }]);
    // Flush currentTricks into the last handHistory entry
    setCurrentTricks(prev => {
      setHandHistory(hh => {
        const copy = [...hh];
        if (copy.length > 0) copy[copy.length - 1] = { ...copy[copy.length - 1], tricks: prev };
        return copy;
      });
      return [];
    });

    if (newScores[0] >= EUCHRE_WIN_SCORE || newScores[1] >= EUCHRE_WIN_SCORE) {
      setPhase("gameOver");
    } else {
      setPhase("handOver");
    }

    // ── Save per-hand stats for authenticated users ──────────────────────────
    if (isAuthenticated && token) {
      const humanTeam = teamOf[mySeat];
      const callerTeam = trumpCaller !== null ? teamOf[trumpCaller] : -1;
      const calledTrump = trumpCaller === mySeat;
      const madeCall = calledTrump && delta[humanTeam] > 0;
      const gotEuchred = calledTrump && delta[humanTeam] === 0;
      // Opponent called and we euchred them (we scored 2 pts without calling)
      const forcedEuchre = !calledTrump && callerTeam !== humanTeam && delta[humanTeam] === 2;
      const lonerAttempted = goingAlone && lonerSeat === mySeat;
      const lonerMade = lonerAttempted && outcome === "loner_march";

      const partnerSeat = (mySeat + 2) % PLAYER_COUNT;
      const partnerIsAi = !isMultiplayer;
      const partnerId = isMultiplayer
        ? (multiplayerPlayers.find(p => p.seatIndex === partnerSeat)?.userId || aiPlayerNames[partnerSeat])
        : aiPlayerNames[partnerSeat];

      fetch(`${API_BASE_URL}/euchre/hand-stats`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ partnerId, partnerIsAi, calledTrump, madeCall, gotEuchred, forcedEuchre, lonerAttempted, lonerMade }),
      }).then(r => { if (!r.ok) r.json().then(d => console.error('hand-stats save failed:', r.status, d)); })
        .catch(err => console.error('hand-stats fetch error:', err));
    }
  }

  function nextHand() {
    const nd = nextPlayer(dealer);
    setDealer(nd);
    dealHand(nd, teamScoresRef.current, handNumber + 1);
  }

  // ─── HUMAN ACTIONS ──────────────────────────────────────────────────────────
  function onHumanTrumpRound1(decision) {
    if (isMultiplayer && !isHost) {
      sendPlayerAction({ type: decision === 'pass' ? 'pass' : 'orderUp' });
      return;
    }
    handleTrumpRound1(decision, mySeat);
  }
  function onHumanTrumpRound2(suit) {
    if (isMultiplayer && !isHost) {
      sendPlayerAction(suit === 'pass' ? { type: 'pass' } : { type: 'callTrump', suit });
      return;
    }
    handleTrumpRound2(suit, mySeat);
  }
  function onHumanGoAlone(alone) {
    if (isMultiplayer && !isHost) {
      sendPlayerAction({ type: 'goAlone', alone });
      return;
    }
    handleGoAlone(alone, mySeat);
  }
  function onHumanDiscard(card) {
    if (whoseTurn !== mySeat || phase !== "discard") return;
    if (isMultiplayer && !isHost) {
      sendPlayerAction({ type: 'discard', card: { rank: card.rank, suit: card.suit } });
      return;
    }
    performDiscard(mySeat, card);
  }
  function onHumanPlayCard(card) {
    if (whoseTurn !== mySeat || phase !== "playing" || trickPauseCards) return;
    const ls = ledSuit();
    if (ls) {
      const canFollow = hands[mySeat].some(c => effectiveSuit(c, trump) === ls);
      if (canFollow && effectiveSuit(card, trump) !== ls) return;
    }
    if (isMultiplayer && !isHost) {
      sendPlayerAction({ type: 'playCard', card: { rank: card.rank, suit: card.suit } });
      return;
    }
    performPlay(mySeat, card);
  }

  // ─── RENDER: CARD (matches BuckEuchre style) ─────────────────────────────────
  function renderCard(card, onClick, dimmed = false, small = false) {
    if (!card) return null;
    const color = SUIT_COLORS[card.suit];
    const w = small ? Math.round(cardSize.w * 0.65) : cardSize.w;
    const h = small ? Math.round(cardSize.h * 0.65) : cardSize.h;
    const rankFontSize = small ? Math.round(cardSize.fontSize * 0.8) : cardSize.fontSize;
    const suitFontSize = small ? Math.round(cardSize.suitSize * 0.8) : cardSize.suitSize;

    return (
      <div
        onClick={onClick}
        style={{
          width: w, height: h,
          borderRadius: isMobile ? 5 : 7,
          background: "#fff",
          border: "2px solid " + (dimmed ? "#444" : "#bbb"),
          boxShadow: dimmed ? "none" : "0 2px 8px rgba(0,0,0,0.35)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          cursor: onClick ? "pointer" : "default",
          opacity: dimmed ? 0.45 : 1,
          userSelect: "none",
          transition: "transform 0.15s, box-shadow 0.15s",
          position: "relative", flexShrink: 0, overflow: "hidden"
        }}
        onMouseEnter={e => { if (onClick && !isMobile) { e.currentTarget.style.transform = "translateY(-8px)"; e.currentTarget.style.boxShadow = "0 8px 18px rgba(0,0,0,0.45)"; } }}
        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = dimmed ? "none" : "0 2px 8px rgba(0,0,0,0.35)"; }}
      >
        <img src="/EuchreLogo.png" alt="" style={{ position: "absolute", width: w * 1.9, height: "auto", opacity: 0.10, pointerEvents: "none", zIndex: 0 }} />
        <div style={{ position: "absolute", top: 2, left: 4, fontSize: rankFontSize, fontWeight: 700, color, lineHeight: 1, zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
          <span>{card.rank}</span>
          <span style={{ fontSize: Math.round(rankFontSize * 0.85) }}>{card.suit}</span>
        </div>
        <div style={{ fontSize: suitFontSize, color, fontWeight: 700, zIndex: 1, WebkitTextStroke: SUIT_STROKE[card.suit] }}>{card.suit}</div>
        <div style={{ position: "absolute", bottom: 2, right: 4, fontSize: rankFontSize, fontWeight: 700, color, transform: "rotate(180deg)", lineHeight: 1, zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
          <span>{card.rank}</span>
          <span style={{ fontSize: Math.round(rankFontSize * 0.85) }}>{card.suit}</span>
        </div>
      </div>
    );
  }

  function renderCardBack(small = false) {
    const w = small ? Math.round(cardSize.w * 0.65) : cardSize.w;
    const h = small ? Math.round(cardSize.h * 0.65) : cardSize.h;
    return (
      <div style={{
        width: w, height: h,
        borderRadius: isMobile ? 5 : 7,
        background: "linear-gradient(135deg, #6a8dd1 0%, #6e98e0 100%)",
        border: "2px solid #0f3460",
        boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
        flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", position: "relative"
      }}>
        <img src="/EuchreLogo.png" alt="" style={{ width: w * 2.0, height: "auto", opacity: 0.7, pointerEvents: "none" }} />
      </div>
    );
  }

  // ─── RENDER: PLAYER HAND (top/bottom — horizontal row) ──────────────────────
  function renderPlayerHand(seat, isInteractive) {
    const h = hands[seat];
    const isLocalPlayer = isMultiplayer ? (seat === mySeat) : (seat === HUMAN);
    const isDiscard = phase === "discard" && seat === dealer && dealer === mySeat;
    const isPlay = phase === "playing" && whoseTurn === mySeat && seat === mySeat;
    const canClick = isLocalPlayer && isInteractive && (isDiscard || isPlay);
    const cardGap = isMobile ? 1 : 4;
    if (!h || h.length === 0) return <div style={{ height: isMobile ? 78 : 100 }} />;
    if (!isLocalPlayer) {
      return (
        <div style={{ display: "flex", gap: cardGap, justifyContent: "center" }}>
          {h.map((_, i) => <div key={i}>{renderCardBack(false)}</div>)}
        </div>
      );
    }
    const sorted = (sortCards && trump) ? sortHandForDisplay(h, trump) : h;
    const onClick = canClick ? (c) => isDiscard ? onHumanDiscard(c) : onHumanPlayCard(c) : null;
    return (
      <div style={{ display: "flex", gap: isMobile ? 2 : 5, justifyContent: "center" }}>
        {sorted.map((c, i) => (
          <div key={cardKey(c) + i}>{renderCard(c, onClick ? () => onClick(c) : null, false, false)}</div>
        ))}
      </div>
    );
  }

  // ─── RENDER: SIDE PLAYER (left/right — vertical fan of card backs) ────────────
  // Uses position:absolute inside a fixed-size wrapper to avoid rotation overflow bugs
  function renderSidePlayer(seat, side) {
    const h = hands[seat];
    const team = teamOf[seat];
    const teamColor = ["#2563eb", "#dc2626"][team];
    const isDealer = dealer === seat;
    const isCaller = trumpCaller === seat;
    const trickCount = tricksWon[seat] || 0;
    const isSittingOut = lonerSeat === seat;

    const cW = cardSize.w; const cH = cardSize.h;
    const gap = 3;
    const numCards = h ? h.length : 5;
    // Card row will be rotated 90deg inside a container sized for the VISUAL result:
    // visual width = cH (card height), visual height = strip width
    const stripWidth = numCards * cW + Math.max(0, numCards - 1) * gap;
    const containerW = cH + 4; // a little padding
    const containerH = Math.max(stripWidth, 120);

    const rot = side === "left" ? 90 : -90;

    // ── Mobile: full-size cards (outer) + vertical nameplate (between cards and table) ──
    // npW must fit the rendered height of renderNameplate() after 90° rotation:
    //   padding(3+3) + text(~12px) + gap(2) + dots(7px) + border(4px) ≈ 31px → use 36px
    if (isMobile) {
      const npW = 36;
      return (
        <div style={{
          display: "flex",
          flexDirection: side === "left" ? "row" : "row-reverse",
          alignItems: "center",
          gap: 0,
          opacity: isSittingOut ? 0.4 : 1,
          flexShrink: 0,
        }}>
          {/* Full-size card backs — same size as top/bottom players */}
          <div style={{ position: "relative", width: containerW, height: containerH, flexShrink: 0 }}>
            {h && h.length > 0 && (
              <div style={{
                position: "absolute", top: "50%", left: "50%",
                transform: `translate(-50%, -50%) rotate(${rot}deg)`,
                display: "flex", gap, transformOrigin: "center",
              }}>
                {h.map((_, i) => <div key={i} style={{ flexShrink: 0 }}>{renderCardBack(false)}</div>)}
              </div>
            )}
          </div>
          {/* Vertical nameplate — exactly renderNameplate() rotated 90° */}
          <div style={{ width: npW, height: containerH, position: "relative", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{
              position: "absolute",
              transform: `rotate(${side === "left" ? -90 : 90}deg)`,
              transformOrigin: "center",
              zIndex: 10,
            }}>
              {renderNameplate(seat)}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        display: "flex",
        flexDirection: side === "left" ? "row" : "row-reverse",
        alignItems: "center",
        gap: 4,
        opacity: isSittingOut ? 0.4 : 1,
      }}>
        {/* Nameplate — rotated 90°, centered in a wrapper wide enough to show it */}
        <div style={{ width: 80, height: containerH, position: "relative", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "visible" }}>
          <div style={{
            position: "absolute",
            transform: `rotate(${side === "left" ? -90 : 90}deg)`,
            transformOrigin: "center center",
            background: "#fff", borderRadius: 8,
            padding: "5px 10px",
            border: `2px solid ${teamColor}`,
            display: "inline-flex", flexDirection: "column", alignItems: "center",
            gap: 2, whiteSpace: "nowrap",
            zIndex: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: teamColor }}>T{team + 1}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{playerLabel(seat)}</span>
              {isDealer && <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: "#facc15", color: "#000", fontWeight: 900, fontSize: 13 }}>D</span>}
              {isCaller && trump && <span style={{ fontSize: 17, color: SUIT_COLORS[trump], fontWeight: 700 }}>{trump}</span>}
            </div>
            {trump && (
              <div style={{ display: "flex", gap: 3 }}>
                {[0,1,2,3,4].map(i => (
                  <div key={i} style={{ width: 11, height: 11, borderRadius: 2, background: i < trickCount ? "#4ade80" : "#cbd5e1", border: "1px solid #94a3b8" }} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Card backs — as a rotated strip inside a fixed-size container */}
        <div style={{ position: "relative", width: containerW, height: containerH, flexShrink: 0 }}>
          {h && h.length > 0 && (
            <div style={{
              position: "absolute",
              top: "50%", left: "50%",
              transform: `translate(-50%, -50%) rotate(${rot}deg)`,
              display: "flex",
              gap,
              transformOrigin: "center",
            }}>
              {h.map((_, i) => <div key={i} style={{ flexShrink: 0 }}>{renderCardBack(false)}</div>)}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── RENDER: NAMEPLATE (top/bottom only) ─────────────────────────────────────
  function renderNameplate(seat) {
    const isDealer = dealer === seat;
    const isCaller = trumpCaller === seat;
    const trickCount = tricksWon[seat] || 0;
    const isSittingOut = lonerSeat === seat;
    const team = teamOf[seat];
    const teamColor = ["#2563eb", "#dc2626"][team];
    const isHuman = seat === mySeat;
    // On mobile, round2 is handled by the fixed overlay — don't expand the nameplate
    const showControls = isHuman && ["round1", "going_alone", "discard"].includes(phase) && !isSittingOut;
    const showControlsDesktop = isHuman && ["round1", "round2", "going_alone", "discard"].includes(phase) && !isSittingOut;
    return (
      <div style={{
        background: "#fff", borderRadius: isMobile ? 5 : 8,
        padding: isMobile ? "3px 6px" : "5px 10px",
        border: `2px solid ${teamColor}`,
        display: "inline-flex", flexDirection: "column", alignItems: "center",
        gap: 2, opacity: isSittingOut ? 0.35 : 1, minWidth: isMobile ? 80 : 110
      }}>
        {/* Name row — trump icon always inline (6a: no separate tall row) */}
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 3 : 6 }}>
          <span style={{ fontSize: isMobile ? 9 : 12, fontWeight: 700, color: teamColor }}>T{team + 1}</span>
          <span style={{ fontSize: isMobile ? 9 : 13, fontWeight: 700, color: "#1e293b" }}>{playerLabel(seat)}</span>
          {isCaller && trump && <span style={{ fontSize: isMobile ? 14 : 22, color: SUIT_COLORS[trump], fontWeight: 700, WebkitTextStroke: SUIT_STROKE[trump], lineHeight: 1 }}>{trump}</span>}
          {isDealer && <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: isMobile ? 14 : 20, height: isMobile ? 14 : 20, borderRadius: "50%", background: "#facc15", color: "#000", fontWeight: 900, fontSize: isMobile ? 9 : 13 }}>D</span>}
        </div>
        {/* Trick dots row */}
        {trump && (
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 2 : 3 }}>
            {[0,1,2,3,4].map(i => (
              <div key={i} style={{ width: isMobile ? 7 : 11, height: isMobile ? 7 : 11, borderRadius: 2, background: i < trickCount ? "#4ade80" : "#cbd5e1", border: "1px solid #94a3b8" }} />
            ))}
          </div>
        )}
        {(isMobile ? showControls : showControlsDesktop) && renderInlineActions()}
      </div>
    );
  }

  function renderInlineActions() {
    if (phase === "round1" && whoseTurn === mySeat) {
      const isMyDealer = dealer === mySeat;
      return (
        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
          <button onClick={() => onHumanTrumpRound1(isMyDealer ? "pick_up" : "order_up")} style={btnStyle("#16a34a")}>{isMyDealer ? "Pick Up" : "Order Up"}</button>
          <button onClick={() => onHumanTrumpRound1("pass")} style={btnStyle("#dc2626")}>Pass</button>
        </div>
      );
    }
    if (phase === "round2" && whoseTurn === mySeat) {
      const available = SUITS.filter(s => s !== turnedUp?.suit);
      const suitBg   = { "♠": "#1e3a8a", "♣": "#166534", "♥": "#dc2626", "♦": "#b91c1c" };
      const suitName = { "♠": "Spades",  "♣": "Clubs",  "♥": "Hearts", "♦": "Diamonds" };
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginTop: 6, width: "100%" }}>
          {available.map(s => (
            <button key={s} onClick={() => onHumanTrumpRound2(s)} style={{
              background: suitBg[s], color: "#fff", border: "2px solid rgba(255,255,255,0.3)",
              borderRadius: 8, padding: isMobile ? "6px 4px" : "8px 6px",
              cursor: "pointer", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 2,
              fontFamily: "inherit",
            }}>
              <span style={{ fontSize: isMobile ? 20 : 24, lineHeight: 1, fontWeight: 700, WebkitTextStroke: "0.5px rgba(255,255,255,0.4)" }}>{s}</span>
              <span style={{ fontSize: isMobile ? 9 : 10, fontWeight: 700, letterSpacing: 0.5, opacity: 0.9 }}>{suitName[s]}</span>
            </button>
          ))}
          {dealer !== mySeat && (
            <button onClick={() => onHumanTrumpRound2("pass")} style={{
              gridColumn: available.length % 2 === 0 ? "1 / -1" : undefined,
              background: "#374151", color: "#d1d5db", border: "1px solid #4b5563",
              borderRadius: 8, padding: isMobile ? "6px" : "8px",
              cursor: "pointer", fontWeight: 700, fontSize: isMobile ? 11 : 13,
              fontFamily: "inherit",
            }}>Pass</button>
          )}
        </div>
      );
    }
    if (phase === "going_alone" && whoseTurn === mySeat) {
      return (
        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
          <button onClick={() => onHumanGoAlone(true)} style={btnStyle("#7c3aed")}>Go Alone</button>
          <button onClick={() => onHumanGoAlone(false)} style={btnStyle("#334155")}>With Partner</button>
        </div>
      );
    }
    if (phase === "discard" && whoseTurn === mySeat) {
      return <div style={{ fontSize: isMobile ? 9 : 11, color: "#6b7280", marginTop: 3 }}>Click a card to discard</div>;
    }
    return null;
  }

  function btnStyle(bg) {
    return { background: bg, color: "#fff", border: "none", borderRadius: 4, padding: isMobile ? "3px 6px" : "4px 10px", fontSize: isMobile ? 9 : 11, fontWeight: 700, cursor: "pointer", fontFamily: "'Georgia', serif" };
  }

  // ─── RENDER: TRICK AREA ───────────────────────────────────────────────────────
  function renderTrickArea() {
    const cards = trickPauseCards || currentTrick;
    // Display positions relative to felt table center
    // displaySeat: 0=bottom, 1=left, 2=top, 3=right
    const positions = {
      0: { bottom: "12%", left: "50%", transform: "translateX(-50%)" },
      1: { top: "50%", left: "18%", transform: "translateY(-50%)" },
      2: { top: "12%", left: "50%", transform: "translateX(-50%)" },
      3: { top: "50%", right: "18%", transform: "translateY(-50%)" },
    };
    return (cards || []).map((t, i) => {
      const dp = displaySeat(t.player);
      return (
        <div key={i} style={{ position: "absolute", ...positions[dp], zIndex: 10 }}>
          {renderCard(t.card, null, false, false)}
        </div>
      );
    });
  }

  // ─── RENDER: FIND DEALER CARDS ───────────────────────────────────────────────
  function renderFindDealerCards() {
    const state = phase === "findDealer" ? findDealerState : phase === "findPartners" ? findPartnerState : null;
    if (!state) return null;
    const jackSeats = phase === "findPartners" ? (findPartnerState?.jacks || []) : [];
    return state.dealt.flatMap((h, seat) =>
      h.map((c, i) => {
        const dp = displaySeat(seat);
        const pos = {
          0: { bottom: 80, left: "50%", transform: "translateX(-50%)" },
          1: { top: "50%", right: 60, transform: "translateY(-50%)" },
          2: { top: 30, left: "50%", transform: "translateX(-50%)" },
          3: { top: "50%", left: 60, transform: "translateY(-50%)" },
        }[dp];
        const isPartnerJack = jackSeats.includes(seat) && i === h.length - 1;
        return (
          <div key={`${seat}-${i}`} style={{ position: "absolute", ...pos, zIndex: 15 }}>
            {renderCard(c, null, false, true)}
            {isPartnerJack && (
              <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", fontSize: 10, fontWeight: 700, color: "#fff", background: "#0ea5e9", borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap" }}>
                {jackSeats.length === 1 ? "Team 1!" : "Partner!"}
              </div>
            )}
          </div>
        );
      })
    );
  }

  // ─── RENDER: KITTY / TRUMP CONTAINER ─────────────────────────────────────────
  function renderKittyContainer() {
    if (!["round1", "round2"].includes(phase)) return null;
    const showTurnedUp = turnedUp && phase === "round1";
    const cW = cardSize.w; const cH = cardSize.h;
    const frame = 3;
    const depth = 4;
    const CW = cW + depth + frame * 2;
    const CH = cH + depth + frame * 2;
    const dp = displaySeat(dealer);

    // Side dealers: use a landscape container (CH×CW) so it stays within the felt.
    // Rotating the whole container with CSS transform shifts DOM bounds outside the table.
    const isLandscape = dp === 1 || dp === 3;
    const cardRotDeg  = dp === 1 ? 90 : dp === 3 ? -90 : 0;

    const containerW = isLandscape ? CH : CW;
    const containerH = isLandscape ? CW : CH;
    // Card backs sized to fill the container naturally (landscape = cH×cW)
    const backW = isLandscape ? cH : cW;
    const backH = isLandscape ? cW : cH;

    // Position the rotated turned-up card so its visual center sits at frame+depth/2
    // from each edge.  DOM element is cW×cH; after ±90° rotation, visual is cH×cW.
    const turnedL = isLandscape ? frame + 2 + (cH - cW) / 2 : frame;
    const turnedT = isLandscape ? frame + 2 - (cH - cW) / 2 : frame;

    const posMap = {
      0: { bottom: 12, left: 12 },
      1: { top: 12, left: 12 },
      2: { top: 12, right: 12 },
      3: { bottom: 12, right: 12 },
    };
    const pos = posMap[dp] || posMap[0];

    return (
      <div style={{
        position: "absolute", ...pos,
        width: containerW, height: containerH,
        background: "#f5f5f5", borderRadius: isMobile ? 6 : 8,
        border: trump ? `2px solid ${SUIT_COLORS[trump]}` : "2px solid rgba(139,92,46,0.5)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        zIndex: 4, padding: `${frame}px`,
        overflow: "hidden",
      }}>
        {[2,1,0].map(i => (
          <div key={i} style={{ position: "absolute", left: frame + i * 2, top: frame + i * 2 }}>
            <div style={{ width: backW, height: backH, borderRadius: isMobile ? 5 : 7,
              background: "linear-gradient(135deg, #6a8dd1 0%, #6e98e0 100%)",
              border: "2px solid #0f3460", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              <img src="/EuchreLogo.png" alt="" style={{ width: backW * 2, height: "auto", opacity: 0.7, pointerEvents: "none" }} />
            </div>
          </div>
        ))}
        {showTurnedUp && (
          <div style={{
            position: "absolute", left: turnedL, top: turnedT, zIndex: 5,
            transform: cardRotDeg ? `rotate(${cardRotDeg}deg)` : undefined,
            transformOrigin: "center",
          }}>
            {renderCard(turnedUp, null, false, false)}
          </div>
        )}
      </div>
    );
  }

  // ─── RENDER: AI COMMENT ───────────────────────────────────────────────────────
  function renderAIComment() {
    if (!aiComment) return null;
    const dp = displaySeat(aiComment.playerIdx);
    const positions = {
      1: { top: "20%", left: "2%" },
      2: { top: "2%", left: "50%", transform: "translateX(-50%)" },
      3: { top: "20%", right: "2%" },
    };
    const pos = positions[dp]; if (!pos) return null;
    const c = aiComment.type === "order" ? { bg: "#f0fdf4", border: "#86efac", text: "#16a34a" } : { bg: "#fef2f2", border: "#fca5a5", text: "#dc2626" };
    return (
      <div style={{ position: "absolute", ...pos, background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 8, padding: "5px 8px", fontSize: isMobile ? 10 : 12, fontStyle: "italic", color: c.text, zIndex: 30, maxWidth: 140, pointerEvents: "none" }}>
        {aiComment.text}
      </div>
    );
  }

  // ─── RENDER: SCORECARD ────────────────────────────────────────────────────────
  function renderScorecard() {
    const sc = s => (s === "♥" || s === "♦") ? "#dc2626" : "#1e293b";
    const scW = isMobile ? 140 : 310;

    // RP progress for current user
    const lifeRP = currentUser?.lifetimeRP || 0;
    const rpProgress = getRankProgress(lifeRP);
    const currentRankName = currentUser?.displayRank || rpProgress.rank.name;
    const nextRankLabel = rpProgress.nextRank
      ? `${rpProgress.pointsToNext} RP until ${rpProgress.nextRank.name}`
      : "Max rank reached!";

    // Last trick in current hand
    const lastTrick = currentTricks.length > 0 ? currentTricks[currentTricks.length - 1] : null;

    return (
      <div style={{ position: "fixed", left: 10, top: 50, width: scW, maxHeight: "calc(100vh - 60px)", zIndex: 20,
        background: "#fef9e7", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.3)", border: "2px solid #d4af37",
        fontFamily: "'Courier New', monospace", fontSize: isMobile ? 11 : 13, color: "#222",
        display: "flex", flexDirection: "column", overflowY: "auto", overflowX: "hidden"
      }}>
        <img src="/EuchreLogo.png" alt="" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: scW - 20, opacity: 0.18, pointerEvents: "none", zIndex: 0 }} />

        {/* Header + scores */}
        <div style={{ padding: "8px 8px 0", position: "relative", zIndex: 1, flexShrink: 0 }}>
          <div style={{ textAlign: "center", fontWeight: 700, marginBottom: 5, fontSize: isMobile ? 12 : 15, borderBottom: "1px solid #888", paddingBottom: 3 }}>EUCHRE</div>
          {[0, 1].map(t => (
            <div key={t} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2, padding: "2px 5px", background: t === 0 ? "#dbeafe" : "#fee2e2", borderRadius: 3 }}>
              <span style={{ fontWeight: 700, fontSize: isMobile ? 9 : 10, maxWidth: isMobile ? 55 : 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{teamLabel(t)}</span>
              <span style={{ fontWeight: 900, fontSize: isMobile ? 14 : 20 }}>{teamScores[t]}</span>
            </div>
          ))}
          <div style={{ fontSize: isMobile ? 9 : 10, color: "#666", textAlign: "center", marginBottom: 3 }}>First to {EUCHRE_WIN_SCORE} wins</div>
          {scoreHistory.length > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderBottom: "1px solid #888", paddingBottom: 2, marginBottom: 2, fontSize: isMobile ? 9 : 10 }}>
              <span style={{ width: "20%" }}>Hnd</span>
              <span style={{ width: "35%", textAlign: "center", color: "#2563eb" }}>T1</span>
              <span style={{ width: "35%", textAlign: "center", color: "#dc2626" }}>T2</span>
            </div>
          )}
        </div>

        {/* Score rows */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 8px", position: "relative", zIndex: 1, minHeight: 30 }}>
          {[...scoreHistory].reverse().map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: isMobile ? 9 : 11, padding: "1px 0" }}>
              <span style={{ width: "20%" }}>{r.hand}</span>
              <span style={{ width: "35%", textAlign: "center", color: "#2563eb" }}>{r.team0Delta > 0 ? `+${r.team0Delta}` : "—"}</span>
              <span style={{ width: "35%", textAlign: "center", color: "#dc2626" }}>{r.team1Delta > 0 ? `+${r.team1Delta}` : "—"}</span>
            </div>
          ))}
        </div>

        {/* Hand history */}
        {handHistory.length > 0 && (
          <div style={{ borderTop: "1px solid #888", background: "#fef3c7", flexShrink: 0 }}>
            <button onClick={() => setShowHandHistory(v => !v)} style={{ width: "100%", background: "none", border: "none", padding: "3px 7px", cursor: "pointer", fontSize: isMobile ? 9 : 11, color: "#92400e", fontWeight: 600, textAlign: "left" }}>
              {showHandHistory ? "▾" : "▸"} Hands ({handHistory.length})
            </button>
            {showHandHistory && (
              <div style={{ maxHeight: 180, overflowY: "auto", padding: "0 7px 5px" }}>
                {[...handHistory].reverse().map((hh, i) => (
                  <details key={i} style={{ marginBottom: 3, fontSize: isMobile ? 9 : 11 }}>
                    <summary style={{ cursor: "pointer", color: "#78350f", fontWeight: 500 }}>
                      #{hh.handNumber} <span style={{ color: sc(hh.trump) }}>{hh.trump}</span> {playerLabel(hh.trumpCaller)}
                    </summary>
                    <div style={{ paddingLeft: 8, color: "#92400e", fontSize: isMobile ? 8 : 10 }}>
                      {(hh.tricks || []).map((trick, ti) => (
                        <div key={ti}>
                          <strong>T{ti+1}:</strong>{" "}
                          {trick.cards.map((c, ci) => (
                            <span key={ci}>{playerLabel(c.player)}: <span style={{ color: sc(c.card.suit) }}>{c.card.rank}{c.card.suit}</span>{ci < trick.cards.length-1 ? " | " : ""}</span>
                          ))}
                          {" → "}<strong>{playerLabel(trick.winner)}</strong>
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Last trick viewer */}
        <div style={{ borderTop: "1px solid #888", background: "#f0fdf4", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 7px" }}>
            <span style={{ fontSize: isMobile ? 9 : 11, color: "#166534", fontWeight: 600 }}>Last Trick</span>
            <div onClick={() => setShowLastTrick(v => !v)} style={{ width: 28, height: 14, borderRadius: 7, background: showLastTrick ? "#22c55e" : "#9ca3af", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: showLastTrick ? 16 : 2, transition: "left 0.2s" }} />
            </div>
          </div>
          {showLastTrick && (
            <div style={{ padding: "0 7px 5px", fontSize: isMobile ? 8 : 10, color: "#166534" }}>
              {lastTrick ? (
                <>
                  {lastTrick.cards.map((c, ci) => (
                    <div key={ci} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontWeight: ci === lastTrick.cards.findIndex(x => x.player === lastTrick.winner) ? 700 : 400 }}>
                        {playerLabel(c.player)}
                      </span>
                      <span style={{ color: sc(c.card.suit), fontWeight: 700 }}>{c.card.rank}{c.card.suit}</span>
                    </div>
                  ))}
                  <div style={{ color: "#15803d", fontWeight: 700, marginTop: 2 }}>Won: {playerLabel(lastTrick.winner)}</div>
                </>
              ) : (
                <div style={{ color: "#9ca3af", fontStyle: "italic" }}>No tricks yet</div>
              )}
            </div>
          )}
        </div>

        {/* Toggles */}
        <div style={{ borderTop: "1px solid #888", padding: "6px 8px", background: "#fef9e7", flexShrink: 0, zIndex: 1 }}>
          {[
            { label: "Speed", state: aiSpeedUp, set: setAiSpeedUp, color: "#22c55e" },
            { label: "Auto Deal", state: autoDeal, set: setAutoDeal, color: "#3b82f6" },
            { label: "Sort Cards", state: sortCards, set: setSortCards, color: "#8b5cf6" },
          ].map(({ label, state, set, color }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: isMobile ? 9 : 10, color: "#555", fontWeight: 600 }}>{label}</span>
              <div onClick={() => set(v => !v)} style={{ width: 28, height: 14, borderRadius: 7, background: state ? color : "#9ca3af", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: state ? 16 : 2, transition: "left 0.2s" }} />
              </div>
            </div>
          ))}
        </div>

        {/* RP Progress */}
        {currentUser && (
          <div style={{ borderTop: "1px solid #c8a000", padding: "6px 8px", background: "#fffbeb", flexShrink: 0, zIndex: 1 }}>
            <div style={{ fontSize: isMobile ? 8 : 10, color: "#92400e", fontWeight: 700, marginBottom: 2 }}>
              {currentRankName}
            </div>
            <div style={{ fontSize: isMobile ? 8 : 10, color: "#78350f", marginBottom: 4 }}>
              {lifeRP.toLocaleString()} Lifetime RP
            </div>
            {rpProgress.nextRank && (
              <>
                <div style={{ height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden", marginBottom: 2 }}>
                  <div style={{ height: "100%", width: `${Math.round(rpProgress.progress * 100)}%`, background: "linear-gradient(90deg, #d4af37, #f59e0b)", borderRadius: 3, transition: "width 0.4s" }} />
                </div>
                <div style={{ fontSize: isMobile ? 7 : 9, color: "#92400e" }}>{nextRankLabel}</div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── GAME OVER ───────────────────────────────────────────────────────────────
  if (phase === "gameOver") {
    const winTeam = teamScores[0] >= EUCHRE_WIN_SCORE ? 0 : 1;
    const humanTeam = teamOf[mySeat];
    const humanWon = humanTeam === winTeam;
    return (
      <div style={{ background: `repeating-linear-gradient(90deg,#3d2817 0px,#4a3423 2px,#5c4033 4px,#4a3423 6px,#3d2817 8px),repeating-linear-gradient(0deg,transparent 0px,transparent 60px,rgba(0,0,0,0.15) 60px,rgba(0,0,0,0.15) 62px),linear-gradient(180deg,#4a3423 0%,#3d2817 50%,#2d1f12 100%)`, backgroundBlendMode: "overlay,overlay,normal", position: "fixed", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Georgia', serif", padding: 20, overflowY: "auto" }}>
        <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 700, color: humanWon ? "#fbbf24" : "#8b949e", marginBottom: 24, textShadow: humanWon ? "0 2px 12px rgba(251,191,36,0.3)" : "none" }}>
          {humanWon ? "Your Team Wins!" : `${teamLabel(winTeam)} Wins!`}
        </div>
        <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
          {[0, 1].map(t => (
            <div key={t} style={{ textAlign: "center", padding: "20px 32px", borderRadius: 12, background: t === winTeam ? "linear-gradient(180deg,#d4af37,#8b6914)" : "#1e293b", border: t === winTeam ? "2px solid #fbbf24" : "1px solid #30363d", minWidth: 110 }}>
              <div style={{ fontSize: 11, color: t === winTeam ? "#000" : "#8b949e", fontWeight: 600, marginBottom: 4 }}>{teamLabel(t)}</div>
              <div style={{ fontSize: 40, fontWeight: 900, color: t === winTeam ? "#000" : "#e2e8f0" }}>{teamScores[t]}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={() => { setTeamScores([0,0]); setScoreHistory([]); setHandHistory([]); setHandNumber(0); handleStart(); }} style={{ background: "#238636", color: "#fff", border: "none", borderRadius: 8, padding: "12px 24px", fontSize: 16, cursor: "pointer", fontFamily: "'Georgia', serif", fontWeight: 700 }}>Play Again</button>
          <button onClick={() => navigate("/home")} style={{ background: "#21262d", color: "#e2e8f0", border: "1px solid #30363d", borderRadius: 8, padding: "12px 24px", fontSize: 16, cursor: "pointer", fontFamily: "'Georgia', serif" }}>Home</button>
        </div>
      </div>
    );
  }

  // Visual seat aliases
  const seatAtBottom = mySeat;
  const seatAtTop    = (mySeat + 2) % PLAYER_COUNT;
  const seatAtLeft   = (mySeat + 1) % PLAYER_COUNT;
  const seatAtRight  = (mySeat + 3) % PLAYER_COUNT;

  // ─── IDLE SCREEN ─────────────────────────────────────────────────────────────
  if (phase === "idle") {
    return (
      <div style={{ background: `repeating-linear-gradient(90deg,#3d2817 0px,#4a3423 2px,#5c4033 4px,#4a3423 6px,#3d2817 8px),repeating-linear-gradient(0deg,transparent 0px,transparent 60px,rgba(0,0,0,0.15) 60px,rgba(0,0,0,0.15) 62px),linear-gradient(180deg,#4a3423 0%,#3d2817 50%,#2d1f12 100%)`, backgroundBlendMode: "overlay,overlay,normal", position: "fixed", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Georgia', serif", overflowY: "auto" }}>
        <div style={{ fontSize: isMobile ? 28 : 42, fontWeight: 700, color: "#e2e8f0", marginBottom: 8 }}>Euchre</div>
        <div style={{ fontSize: 14, color: "#8b949e", marginBottom: 32 }}>Partner card game · First team to {EUCHRE_WIN_SCORE} wins</div>
        {partnerMode === 'jacks' ? (
          <div style={{ background: "#161b22", borderRadius: 12, padding: 20, marginBottom: 24, border: "1px solid #0ea5e9", width: isMobile ? 280 : 360 }}>
            <div style={{ color: "#0ea5e9", fontSize: 13, marginBottom: 4, fontWeight: 700 }}>🃏 First 2 Jacks = Partners</div>
            <div style={{ color: "#8b949e", fontSize: 12 }}>Cards will be dealt one at a time. The first two players to receive a Jack are partners (Team 1). The other two are Team 2.</div>
          </div>
        ) : (
          <div style={{ background: "#161b22", borderRadius: 12, padding: 20, marginBottom: 24, border: "1px solid #30363d", width: isMobile ? 280 : 360 }}>
            <div style={{ color: "#8b949e", fontSize: 13, marginBottom: 8, fontWeight: 600 }}>Teams</div>
            {[0, 1].map(t => (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: t === 0 ? "1px solid #21262d" : "none" }}>
                <div style={{ width: 12, height: 12, borderRadius: 2, background: t === 0 ? "#2563eb" : "#dc2626" }} />
                <span style={{ color: "#c9d1d9", fontSize: 14 }}>{teamLabel(t)}</span>
              </div>
            ))}
          </div>
        )}
        {(!isMultiplayer || isHost) && (
          <button onClick={handleStart} style={{ background: "linear-gradient(135deg,#238636,#1a6b2a)", color: "#fff", border: "none", borderRadius: 10, padding: "16px 40px", fontSize: 20, fontWeight: 700, cursor: "pointer", fontFamily: "'Georgia', serif", letterSpacing: 1 }}>
            {partnerMode === 'jacks' ? 'Find Partners' : 'Deal Cards'}
          </button>
        )}
        {isMultiplayer && !isHost && (
          <div style={{ color: "#8b949e", fontSize: 14, fontStyle: "italic" }}>Waiting for host to deal...</div>
        )}
        <button onClick={() => navigate("/home")} style={{ marginTop: 12, background: "transparent", color: "#8b949e", border: "none", cursor: "pointer", fontSize: 13 }}>← Back to Home</button>
      </div>
    );
  }

  // ─── TABLE DIMENSIONS ────────────────────────────────────────────────────────
  // Mobile: width leaves room for full-size card strips (82px) + nameplate (22px) each side
  // Height uses available vertical space between top/bottom player areas and the bottom bar
  // Mobile: 82px cards + 36px nameplate each side + 4px gaps = 240px overhead
  const feltW = isMobile ? Math.max(140, window.innerWidth - 244) : 580;
  const feltH = isMobile ? Math.min(420, Math.max(240, window.innerHeight - 360)) : 400;

  // ─── MAIN TABLE — grid layout (top/middle-row/bottom) ───────────────────────
  return (
    <div style={{ background: `repeating-linear-gradient(90deg,#3d2817 0px,#4a3423 2px,#5c4033 4px,#4a3423 6px,#3d2817 8px),repeating-linear-gradient(0deg,transparent 0px,transparent 60px,rgba(0,0,0,0.15) 60px,rgba(0,0,0,0.15) 62px),linear-gradient(180deg,#4a3423 0%,#3d2817 50%,#2d1f12 100%)`, backgroundBlendMode: "overlay,overlay,normal", position: "fixed", inset: 0, overflow: "hidden", fontFamily: "'Georgia', serif", userSelect: "none" }}>
      {!isMobile && renderScorecard()}

      {/* Mobile scorecard overlay */}
      {isMobile && showScorecardOverlay && (
        <div style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-end" }}
          onClick={() => setShowScorecardOverlay(false)}>
          <div style={{ width: "100%", maxHeight: "80vh", overflowY: "auto", background: "#fef9e7", borderRadius: "16px 16px 0 0", border: "2px solid #d4af37" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px 4px", borderBottom: "1px solid #888" }}>
              <span style={{ fontFamily: "'Courier New', monospace", fontWeight: 700, fontSize: 15, color: "#222" }}>EUCHRE</span>
              <button onClick={() => setShowScorecardOverlay(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#555", lineHeight: 1 }}>&times;</button>
            </div>
            <div style={{ padding: "8px 14px", fontFamily: "'Courier New', monospace" }}>
              {[0, 1].map(t => (
                <div key={t} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, padding: "4px 8px", background: t === 0 ? "#dbeafe" : "#fee2e2", borderRadius: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#222" }}>{teamLabel(t)}</span>
                  <span style={{ fontWeight: 900, fontSize: 22, color: "#222" }}>{teamScores[t]}</span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: "#666", textAlign: "center", marginBottom: 6 }}>First to {EUCHRE_WIN_SCORE} wins</div>
              {scoreHistory.length > 0 && (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderBottom: "1px solid #888", paddingBottom: 3, marginBottom: 3, fontSize: 11 }}>
                    <span style={{ width: "20%" }}>Hnd</span>
                    <span style={{ width: "35%", textAlign: "center", color: "#2563eb" }}>T1</span>
                    <span style={{ width: "35%", textAlign: "center", color: "#dc2626" }}>T2</span>
                  </div>
                  {[...scoreHistory].reverse().map((r, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "2px 0" }}>
                      <span style={{ width: "20%" }}>{r.hand}</span>
                      <span style={{ width: "35%", textAlign: "center", color: "#2563eb" }}>{r.team0Delta > 0 ? `+${r.team0Delta}` : "—"}</span>
                      <span style={{ width: "35%", textAlign: "center", color: "#dc2626" }}>{r.team1Delta > 0 ? `+${r.team1Delta}` : "—"}</span>
                    </div>
                  ))}
                </>
              )}
              {/* Toggles */}
              <div style={{ borderTop: "1px solid #888", marginTop: 8, paddingTop: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
                {[
                  { label: "Speed", state: aiSpeedUp, set: setAiSpeedUp, color: "#22c55e" },
                  { label: "Auto Deal", state: autoDeal, set: setAutoDeal, color: "#3b82f6" },
                  { label: "Sort Cards", state: sortCards, set: setSortCards, color: "#8b5cf6" },
                ].map(({ label, state, set, color }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12, color: "#555", fontWeight: 600 }}>{label}</span>
                    <div onClick={() => set(v => !v)} style={{ width: 32, height: 16, borderRadius: 8, background: state ? color : "#9ca3af", cursor: "pointer", position: "relative" }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: state ? 18 : 2, transition: "left 0.2s" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {dealerWinBanner && (
        <div style={{ position: "fixed", top: "40%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 100, background: "#fbbf24", borderRadius: 12, padding: "16px 32px", color: "#000", fontWeight: 700, fontSize: 20 }}>
          {dealerWinBanner}
        </div>
      )}

      {/* Outer wrapper */}
      <div style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: isMobile ? "flex-start" : "center",
        paddingTop: isMobile ? 6 : 0,
        paddingBottom: isMobile ? 80 : 0,
        boxSizing: "border-box",
        gap: isMobile ? 2 : 6,
      }}>

        {/* TOP PLAYER — hand above nameplate so nameplate sits between cards and table (6d) */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? 2 : 4 }}>
          {renderPlayerHand(seatAtTop, false)}
          {renderNameplate(seatAtTop)}
        </div>

        {/* MIDDLE ROW: left player | felt table | right player */}
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: isMobile ? 2 : 8, overflow: "visible" }}>

          {/* LEFT PLAYER */}
          {renderSidePlayer(seatAtLeft, "left")}

          {/* FELT TABLE */}
          <div style={{
            width: feltW, height: feltH,
            borderRadius: isMobile ? 16 : 24,
            background: "radial-gradient(ellipse at center, #1b6b45 0%, #14532d 45%, #0f3d20 100%)",
            border: isMobile ? "5px solid #3b2414" : "8px solid #3b2414",
            boxShadow: "inset 0 0 40px rgba(0,0,0,0.5), 0 6px 24px rgba(0,0,0,0.5)",
            position: "relative", flexShrink: 0,
          }}>
            <div style={{ position: "absolute", inset: isMobile ? 3 : 6, borderRadius: isMobile ? 12 : 18, border: "1px dashed rgba(255,255,255,0.07)", pointerEvents: "none" }} />
            {/* Watermark logo */}
            <img src="/EuchreLogoWhite.png" alt="" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: isMobile ? feltW - 10 : 380, height: "auto", opacity: 0.2, pointerEvents: "none", zIndex: 1 }} />
            {/* Kitty/trump container */}
            {phase !== "dealing" && renderKittyContainer()}
            {renderTrickArea()}
            {renderFindDealerCards()}
            {/* Mobile round2 trump selector — inside the table so it never covers the player's hand */}
            {isMobile && phase === "round2" && whoseTurn === mySeat && (() => {
              const available = SUITS.filter(s => s !== turnedUp?.suit);
              const suitBg = { "♠": "#1e3a8a", "♣": "#166534", "♥": "#dc2626", "♦": "#b91c1c" };
              const suitName = { "♠": "Spades", "♣": "Clubs", "♥": "Hearts", "♦": "Diamonds" };
              return (
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 20, background: "rgba(10,16,26,0.96)", border: "1px solid #475569", borderRadius: 10, padding: "8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, width: "88%" }}>
                  {available.map(s => (
                    <button key={s} onClick={() => onHumanTrumpRound2(s)} style={{ background: suitBg[s], color: "#fff", border: "2px solid rgba(255,255,255,0.25)", borderRadius: 7, padding: "8px 4px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, fontFamily: "inherit" }}>
                      <span style={{ fontSize: 20, lineHeight: 1, fontWeight: 700 }}>{s}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, opacity: 0.85 }}>{suitName[s]}</span>
                    </button>
                  ))}
                  {dealer !== mySeat && (
                    <button onClick={() => onHumanTrumpRound2("pass")} style={{ gridColumn: available.length % 2 === 0 ? "1 / -1" : undefined, background: "#374151", color: "#d1d5db", border: "1px solid #4b5563", borderRadius: 7, padding: "7px", cursor: "pointer", fontWeight: 700, fontSize: 12, fontFamily: "inherit" }}>Pass</button>
                  )}
                </div>
              );
            })()}
            {/* Hand result banner centered in table (6e) */}
            {handResult && phase === "handOver" && (
              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 50, background: "#161b22", border: "1px solid #d4af37", borderRadius: 10, padding: "10px 24px", color: "#fbbf24", fontWeight: 700, fontSize: isMobile ? 13 : 15, textAlign: "center", whiteSpace: "nowrap" }}>
                {OUTCOME_LABELS[handResult.outcome] || handResult.outcome}
              </div>
            )}
          </div>

          {/* RIGHT PLAYER */}
          {renderSidePlayer(seatAtRight, "right")}
        </div>

        {/* BOTTOM PLAYER */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          {renderNameplate(seatAtBottom)}
          {renderPlayerHand(seatAtBottom, true)}
        </div>
      </div>

      {/* Next hand button — desktop only */}
      {!isMobile && phase === "handOver" && !autoDeal && (
        <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", zIndex: 50 }}>
          {(!isMultiplayer || isHost) ? (
            <button onClick={nextHand} style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 8, padding: "12px 32px", fontSize: 16, cursor: "pointer", fontFamily: "'Georgia', serif", fontWeight: 700 }}>Next Hand</button>
          ) : (
            <span style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>Waiting for host...</span>
          )}
        </div>
      )}

      {/* Home button — desktop only */}
      {!isMobile && (
        <div style={{ position: "fixed", top: 10, right: 10, zIndex: 30 }}>
          <button onClick={() => navigate("/home")} style={{ background: "#21262d", color: "#8b949e", border: "1px solid #30363d", borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer" }}>Home</button>
        </div>
      )}

      {/* Mobile bottom bar */}
      {isMobile && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "#161b22", borderTop: "2px solid #d4af37",
          zIndex: 30, paddingBottom: "env(safe-area-inset-bottom, 0px)"
        }}>
          {/* Settings drawer */}
          {showMobileSettings && (
            <div style={{
              background: "#0d1117", borderBottom: "1px solid #30363d",
              padding: "8px 12px", display: "flex", flexWrap: "wrap",
              alignItems: "center", gap: 10, justifyContent: "space-between"
            }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {/* Speed */}
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>Speed</span>
                  <div onClick={() => setAiSpeedUp(v => !v)} style={{ width: 30, height: 16, borderRadius: 8, background: aiSpeedUp ? "#22c55e" : "#4b5563", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: aiSpeedUp ? 16 : 2, transition: "left 0.2s" }} />
                  </div>
                </div>
                {/* Auto Deal */}
                {(!isMultiplayer || isHost) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>Auto Deal</span>
                    <div onClick={() => setAutoDeal(v => !v)} style={{ width: 30, height: 16, borderRadius: 8, background: autoDeal ? "#7c3aed" : "#4b5563", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: autoDeal ? 16 : 2, transition: "left 0.2s" }} />
                    </div>
                  </div>
                )}
                {/* Score history button */}
                <button onClick={() => setShowScorecardOverlay(v => !v)} style={{ background: "#d4af3720", border: "1px solid #d4af37", color: "#d4af37", borderRadius: 4, padding: "3px 10px", fontSize: 11, cursor: "pointer", fontWeight: 700 }}>Full Score</button>
              </div>
              <button onClick={() => navigate("/home")} style={{ background: "none", border: "1px solid #334155", color: "#94a3b8", borderRadius: 4, padding: "2px 10px", fontSize: 11, cursor: "pointer" }}>
                Exit
              </button>
            </div>
          )}
          {/* Main bar: team scores + ⚙ button */}
          <div style={{ padding: "5px 8px", display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {/* Team 0 score */}
              <div style={{ flex: 1, textAlign: "center", background: "#1e3a5f", borderRadius: 4, padding: "3px 4px" }}>
                <div style={{ fontSize: 9, color: "#60a5fa", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{teamLabel(0)}</div>
                <div style={{ fontSize: 17, fontWeight: 900, color: "#fbbf24", lineHeight: 1.1 }}>{teamScores[0]}</div>
              </div>
              {/* Trump / vs separator */}
              <div style={{ minWidth: 22, textAlign: "center" }}>
                {trump
                  ? <span style={{ color: SUIT_COLORS[trump], fontSize: 16, fontWeight: 700 }}>{trump}</span>
                  : <span style={{ color: "#484f58", fontSize: 12 }}>vs</span>
                }
              </div>
              {/* Team 1 score */}
              <div style={{ flex: 1, textAlign: "center", background: "#3b1f1f", borderRadius: 4, padding: "3px 4px" }}>
                <div style={{ fontSize: 9, color: "#f87171", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{teamLabel(1)}</div>
                <div style={{ fontSize: 17, fontWeight: 900, color: "#fbbf24", lineHeight: 1.1 }}>{teamScores[1]}</div>
              </div>
              {/* Settings toggle */}
              <button
                onClick={() => setShowMobileSettings(v => !v)}
                style={{
                  background: showMobileSettings ? "#21262d" : "none",
                  border: "1px solid " + (showMobileSettings ? "#d4af37" : "#30363d"),
                  color: showMobileSettings ? "#d4af37" : "#94a3b8",
                  borderRadius: 6, width: 32, height: 32, fontSize: 16,
                  cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center"
                }}
                title="Settings"
              >⚙</button>
            </div>
            {/* Next Hand button */}
            {phase === "handOver" && !autoDeal && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                {(!isMultiplayer || isHost) ? (
                  <button onClick={nextHand} style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 6, padding: "6px 24px", fontSize: 14, cursor: "pointer", fontFamily: "'Georgia', serif", fontWeight: 700 }}>Next Hand</button>
                ) : (
                  <span style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>Waiting for host...</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
